/**
 * How long until the next daily, as the Play tab's Daily card shows it.
 *
 * The server starts a new daily at midnight on its own clock, and since
 * `docs/decisions/0052` it says when that is: `DailyInfo.nextRolloverAtMs`, an
 * instant. A countdown to an instant is exact on any device in any timezone,
 * so when the field is there the countdown runs to it.
 *
 * When it is not — an older server, or an answer cached from one — this falls
 * back to what it did before: the server says only which calendar day it is on
 * (`DailyInfo.date`), so the countdown runs to the next midnight on **this
 * device**, and only while the device and the server agree on what day it is.
 * In the hours where they do not, a countdown would be wrong, and none is
 * shown.
 *
 * Pure: the clock is a parameter.
 */

/** The local calendar day of `now`, as `YYYY-MM-DD`: the same shape the server's `date` has. */
export function localDayKey(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${String(now.getFullYear())}-${month}-${day}`;
}

/** Milliseconds from `now` to the next local midnight. Always more than zero. */
export function msUntilLocalMidnight(now: Date): number {
  const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return next.getTime() - now.getTime();
}

/** "7h 12m" or "45m". Rounded up to the minute, so it never reads zero while time is left. */
export function formatCountdown(ms: number): string {
  const minutes = Math.ceil(Math.max(0, ms) / 60_000);
  if (minutes <= 1) return '1m';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours === 0 ? `${String(rest)}m` : `${String(hours)}h ${String(rest).padStart(2, '0')}m`;
}

/**
 * The longest a day can have left: twenty-five hours, the day the clocks go
 * back. An instant further off than that is not this daily's rollover — it is
 * a device whose clock is wrong, or a server's — and is not counted down to.
 */
const LONGEST_DAY_MS = 25 * 60 * 60_000;

/**
 * The server's rollover instant, when it can be counted down to as it is:
 * still ahead of `nowMs`, and no further ahead than a day can be long. The
 * Daily tab hands such an instant to the shell's `ChallengeCard` (`endsAt`),
 * which keeps its own clock; anything else goes through
 * {@link nextDailyCountdown}'s fallback.
 */
export function usableRollover(nextRolloverAtMs: number | undefined, nowMs: number): number | null {
  if (typeof nextRolloverAtMs !== 'number' || !Number.isFinite(nextRolloverAtMs)) return null;
  const left = nextRolloverAtMs - nowMs;
  return left > 0 && left <= LONGEST_DAY_MS ? nextRolloverAtMs : null;
}

/**
 * The countdown to show for a daily dated `serverDate`, or `null` when none
 * can honestly be shown.
 *
 * `nextRolloverAtMs` is the server's own instant for the next daily. Given
 * one that is still ahead, and no further ahead than a day can be long, the
 * countdown is to it. One that has already passed means the board on screen
 * is yesterday's, and nothing is shown until it is fetched again. Anything
 * else falls back to this device's own midnight.
 */
export function nextDailyCountdown(
  serverDate: string,
  now: Date,
  nextRolloverAtMs?: number,
): string | null {
  if (typeof nextRolloverAtMs === 'number' && Number.isFinite(nextRolloverAtMs)) {
    const left = nextRolloverAtMs - now.getTime();
    if (left <= 0) return null;
    if (left <= LONGEST_DAY_MS) return formatCountdown(left);
  }
  if (serverDate !== localDayKey(now)) return null;
  return formatCountdown(msUntilLocalMidnight(now));
}

/** `localDayKey` for an instant given in milliseconds: for a caller that keeps time as a number. */
export function localDayKeyAt(ms: number): string {
  return localDayKey(new Date(ms));
}
